import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { withSnackbar } from 'notistack';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import { TextField } from '@iris-platform/iris-react-ui';
import FormHelperText from '@material-ui/core/FormHelperText';
import Button from '@material-ui/core/Button';
import { withApollo } from 'react-apollo';
import {
  isEmpty,
} from 'ramda';
import { UPDATE_APN_AUTH_TOKEN } from '../queries';
import { stylesToPropType, handleChange } from '../../../utils';

const styles = () => ({
  root: {
    width: '100%',
  },
  saveButton: {
    borderRadius: '0.125em',
    marginTop: '1em',
    width: '8em',
  },
});

class ApnTokenAuth extends Component {
  state = {
    ApnAuthTokenError: null,
    loading: true,
    error: null,
    apnsTeamId: '',
    apnsKeyId: '',
    apnsPrivKey: '',
    isProduction: false,
  }

  reader = null;

  apnsPrivKeyRef = React.createRef();

  saveChanges = async () => {
    const {
      client,
      appType,
      childAppName,
      domain,
      enqueueSnackbar,
    } = this.props;
    const {
      apnsTeamId,
      apnsKeyId,
      apnsPrivKey,
      isProduction,
    } = this.state;


    const isTeamIDInvalid = isEmpty(apnsTeamId);
    const isKeyIDInvalid = isEmpty(apnsKeyId);
    const isPrivateKeyInvalid = isEmpty(apnsPrivKey);
    const hasError = isTeamIDInvalid || isKeyIDInvalid || isPrivateKeyInvalid;

    const appDomainClientID = appType === 'child' ? `${domain}-${childAppName}` : domain;
    const apnAuthToken = {
      app_domain_client_id: appDomainClientID,
      domain,
      apns_team_id: apnsTeamId,
      apns_key_id: apnsKeyId,
      apns_priv_key: apnsPrivKey,
      apns_production: isProduction,
    };

    if (hasError) {
      const update = {
        ApnAuthTokenError: isTeamIDInvalid || isKeyIDInvalid || isPrivateKeyInvalid ? new Error('Auth Token APN error') : null,
        error: new Error('Please fix all errors before saving'),
      };
      enqueueSnackbar('Failed to register key', { variant: 'error' });
      this.setState(update);
    } else {
      this.setState({ loading: true });

      try {
        await client.mutate({
          mutation: UPDATE_APN_AUTH_TOKEN,
          variables: { apnAuthToken },
        });
        enqueueSnackbar('Key successfully registered', { variant: 'success' });
        this.setState({ loading: false, error: null });
      } catch (error) {
        enqueueSnackbar('Failed to register key', { variant: 'error' });
        this.setState({ error: new Error('Unable to save changes, please try again later') });
      } finally {
        this.setState({
          loading: false,
          apnsTeamId: '',
          apnsKeyId: '',
          apnsPrivKey: '',
          isProduction: false,
        });

        this.apnsPrivKeyRef.current.value = null;
      }
    }
  }

  handleFileRead = (e) => {
    const content = this.reader.result;
  };


  handlePrivKeyFile = (file) => {
    this.reader = new FileReader();
    this.reader.onloadend = this.handleFileRead;
    this.reader.onload = () => {
      const apnsPrivKey = this.reader.result;
      this.setState({ apnsPrivKey });
    };
    this.reader.readAsText(file);
  };

  toggleProduction = () => this.setState(prevState => ({
    isProduction: !prevState.isProduction, error: null,
  }));

  render() {
    const { classes } = this.props;
    const {
      ApnAuthTokenError,
      error,
      loading,
      apnsTeamId,
      apnsKeyId,
      apnsPrivKey,
      isProduction,
    } = this.state;

    return (
      <Fragment>
        <form>
          <FormControl className={classes.root} error={!!ApnAuthTokenError}>
            <TextField
              label="APN Token Auth"
              value={apnsTeamId || ''}
              onChange={(event) => {
                handleChange(this, ['apnsTeamId'])(event);
                this.setState({
                  ApnAuthTokenError: null,
                });
              }}
              type="text"
              placeholder="Team Id"
              InputLabelProps={{ shrink: true }}
              margin="normal"
            />
            {ApnAuthTokenError && <FormHelperText>{ApnAuthTokenError.message}</FormHelperText>}
          </FormControl>
          <br />
          <FormControl className={classes.root} error={!!ApnAuthTokenError}>
            <TextField
              label="APN Token Auth"
              value={apnsKeyId || ''}
              onChange={(event) => {
                handleChange(this, ['apnsKeyId'])(event);
                this.setState({
                  ApnAuthTokenError: null,
                });
              }}
              type="text"
              placeholder="Key ID"
              InputLabelProps={{ shrink: true }}
              margin="normal"
            />
            {ApnAuthTokenError && <FormHelperText>{ApnAuthTokenError.message}</FormHelperText>}
          </FormControl>
          <br />
          <FormControl className={classes.root} error={!!ApnAuthTokenError}>
            <TextField
              label="Private key file (.p8)"
              onChange={(event) => {
                this.handlePrivKeyFile(event.target.files[0]);
                this.setState({
                  ApnAuthTokenError: null,
                  error: null,
                });
              }}
              type="file"
              placeholder="Key"
              InputLabelProps={{ shrink: true }}
              margin="normal"
              inputRef={this.apnsPrivKeyRef}
            />
            {ApnAuthTokenError && <FormHelperText>{ApnAuthTokenError.message}</FormHelperText>}
          </FormControl>
          <br />
          <FormControlLabel
            control={(
              <Switch
                checked={isProduction}
                onChange={this.toggleProduction}
                value="enable-selfprovision"
                color="primary"
                disabled={false}
              />
            )}
            label={isProduction ? 'Production' : 'Development'}
          />
          <br />
          <FormControl className={classes.root} error={!!error}>
            <Button
              variant="contained"
              classes={{ root: classes.saveButton }}
              disabled={apnsTeamId === '' || apnsKeyId === '' || apnsPrivKey === ''}
              onClick={this.saveChanges}
            >
                Register
            </Button>
            {error && <FormHelperText>{error.message}</FormHelperText>}
          </FormControl>
        </form>
      </Fragment>
    );
  }
}

ApnTokenAuth.propTypes = {
  appType: PropTypes.string.isRequired,
  childAppName: PropTypes.string.isRequired,
  domain: PropTypes.string.isRequired,
  classes: stylesToPropType(styles()).isRequired,
  client: PropTypes.object.isRequired,
};

export default withSnackbar(withStyles(styles)(withApollo(ApnTokenAuth)));
